import { type FC, type InputHTMLAttributes, type Ref } from 'react'

import { useField } from 'formik'
import styled from 'styled-components'

import colors from 'styles/colors'

type FormikInputFieldProps = {
    type?: string
    label: string
    name: string
    value?: string | number | null
    customClass?: string
    externalInputRef?: Ref<HTMLInputElement>
    inputProps?: InputHTMLAttributes<HTMLInputElement>
    showRequiredErrors?: boolean
}

export const FormikInputField: FC<FormikInputFieldProps> = ({
    type = 'text',
    label,
    name,
    value,
    customClass,
    externalInputRef,
    inputProps,
    showRequiredErrors = true
}) => {
    const [field, meta] = useField({ name, type })

    const filled = !!(field.value ?? value)

    const normalizedError =
        typeof meta.error === 'string' ? meta.error.trim().toLowerCase() : ''

    const isRequiredError = normalizedError === 'required'

    const hasError = !!(
        meta.touched &&
        meta.error &&
        (!isRequiredError || showRequiredErrors)
    )

    return (
        <FieldWrapper className={customClass}>
            <InputContainer>
                <InputLabel $filled={filled} htmlFor={name}>
                    {label}
                </InputLabel>

                <Input
                    {...field}
                    id={name}
                    ref={externalInputRef}
                    type={type}
                    value={field.value ?? ''}
                    $filled={filled}
                    $hasError={hasError}
                    autoComplete={inputProps?.autoComplete}
                    onChange={e => {
                        field.onChange(e)
                        inputProps?.onChange?.(e)
                    }}
                    onBlur={e => {
                        field.onBlur(e)
                        inputProps?.onBlur?.(e)
                    }}
                    onFocus={e => {
                        inputProps?.onFocus?.(e)
                    }}
                />
            </InputContainer>

            {hasError ? (
                <ErrorMessageComponent>{meta.error}</ErrorMessageComponent>
            ) : null}
        </FieldWrapper>
    )
}

interface InputProps {
    $filled: boolean
    $hasError?: boolean
}

const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 1rem;
`

const InputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 7rem;
    background: ${colors.white89deg};
    border: 0.1rem solid #dddddd;
    box-sizing: border-box;
    box-shadow: ${colors.shadowBlack};
    border-radius: 2rem;
`

const InputLabel = styled.label<InputProps>`
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 1.5rem;
    font-weight: 500;
    position: absolute;
    left: 2rem;
    color: #b0b0b0;
    pointer-events: none;
    transition: 0.2s all;
    transform: translateY(0);
    z-index: 1;

    ${props => !!props.$filled && 'top: 1.6rem;'}
`

const Input = styled.input<InputProps>`
    font-weight: 600;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 1.7rem;
    text-indent: 1.8rem;
    height: 100%;
    width: 100%;
    border-radius: 2rem;
    border: 0.2rem solid transparent;
    transition:
        border 0.2s,
        padding 0.2s;

    ${props => !!props.$filled && 'padding-top: 2rem;'}
    ${props => props.$hasError && `border-color: ${colors.red};`}
`

const ErrorMessageComponent = styled.div`
    margin-top: 0.6rem;
    padding-left: 2rem;
    padding-right: 2rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    font-size: 1.2rem;
    line-height: 1.5rem;
    color: ${colors.red};
`